"use client";

import * as React from "react";
import { Textarea } from "@shared/src/components/ui/textarea";
import { cn } from "@shared/src/utils/cn";

interface CustomTextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: boolean;
  className?: string;
  containerClassName?: string;
  bottomComponents?: React.ReactNode;
  required?: boolean;
  hint?: string;
}

export const CustomTextArea = React.forwardRef<HTMLTextAreaElement, CustomTextAreaProps>(
  ({ className, label, error, containerClassName, bottomComponents, required = false, hint, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);

    return (
      <div className="space-y-1.5">
        <div
          className={cn(
            "relative w-full min-h-[120px] rounded-2xl border border-border-tertiary bg-surfaces-bg-primary transition-all duration-200",
            error ? "border-border-error" : isFocused && "border-border-focus",
            containerClassName,
          )}
        >
          <label
            className={cn(
              "absolute -top-2 left-3 !text-small bg-surfaces-bg-primary px-2 transition-all duration-200",
              error ? "text-text-error" : isFocused ? "text-text-brand" : "text-text-tertiary",
            )}
          >
            {label}
            {required && <span className="text-small-2 text-text-error ml-1">*</span>}
          </label>
          <div className="px-4 py-4">
            <Textarea
              {...props}
              ref={ref}
              onFocus={e => {
                setIsFocused(true);
                props.onFocus?.(e);
              }}
              // autoFocus
              onBlur={e => {
                setIsFocused(false);
                props.onBlur?.(e);
              }}
              className={cn(
                "border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-text-primary placeholder:text-text-tertiary resize-none min-h-[100px] px-0 max-sm:placeholder:text-small max-sm:text-small",
                className,
              )}
            />
          </div>
          {bottomComponents && <div className="px-4 mb-3">{bottomComponents}</div>}
        </div>
        {hint && <p className="text-small text-text-tertiary ml-4">{hint}</p>}
      </div>
    );
  },
);

CustomTextArea.displayName = "CustomTextArea";
